import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Mail, ArrowRight, Calculator } from "lucide-react";
import { MarketingLayout } from "@/components/marketing/MarketingLayout";
import { PageHero } from "@/components/marketing/primitives";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/DatePicker";
import { OptionValuationView } from "@/components/equity/OptionValuationView";
import { ExercisePlanner } from "@/components/equity/ExercisePlanner";
import { CalculatorExplainer } from "@/components/equity/CalculatorExplainer";
import api from "@/lib/api";

const START = {
  post_money: 40_000_000, fd_shares: 10_000_000, fmv: 1.2, preferred_invested: 8_000_000, liquidation_multiple: 1,
  options: 20_000, strike: 0.9, option_type: "ISO", vesting_start: "", vesting_months: 48, cliff_months: 12,
};
const DEFAULT_ASSUMPTIONS = { expected_dilution_pct: 20, exit_years: 4, volatility_pct: 60, risk_free_pct: 4, expected_term_years: 6.25, tax_mode: "us", ordinary_rate_pct: 37, ltcg_rate_pct: 23.8, amt_rate_pct: 28, state_rate_pct: 0, simple_rate_pct: 30, exit_values: [] };

function toPayload(f, assumptions) {
  const pref_price = f.post_money && f.fd_shares ? f.post_money / f.fd_shares : 0;
  const pref_shares = pref_price ? (f.preferred_invested || 0) / pref_price : 0;
  return {
    company: { fmv: f.fmv, preferred_price: pref_price, fd_shares: f.fd_shares,
      preferred_classes: pref_shares > 0 ? [{ name: "Preferred", shares: pref_shares, price_per_share: pref_price, liquidation_multiple: f.liquidation_multiple || 1 }] : [] },
    grant: { options: f.options, strike: f.strike, option_type: f.option_type, vesting_start: f.vesting_start || null, vesting_months: f.vesting_months, cliff_months: f.cliff_months, expiry_years: 10 },
    assumptions,
  };
}

function Num({ label, k, form, set, step = "any", prefix, testid, hint }) {
  return (
    <div>
      <Label htmlFor={testid} className="text-xs">{label}</Label>
      <div className="relative mt-1">
        {prefix && <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-xs text-muted-foreground">{prefix}</span>}
        <Input id={testid} type="number" step={step} min={0} value={form[k] ?? ""} onChange={(e) => set(k, e.target.value === "" ? "" : Number(e.target.value))} className={`h-9 text-sm ${prefix ? "pl-6" : ""}`} data-testid={testid} />
      </div>
      {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

export default function OptionCalculator() {
  const [form, setForm] = useState(START);
  const [assumptions, setAssumptions] = useState(DEFAULT_ASSUMPTIONS);
  const [valuation, setValuation] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lead, setLead] = useState({ email: "", name: "" });
  const [leadBusy, setLeadBusy] = useState(false);
  const [leadDone, setLeadDone] = useState(false);
  const [leadStatus, setLeadStatus] = useState("");
  const timer = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setBusy(true);
      try {
        const { data } = await api.post("/public/option-valuation", toPayload(form, assumptions));
        setValuation(data);
        if (!assumptions.exit_values?.length) setAssumptions((a) => ({ ...a, exit_values: data.scenarios.map((s) => Math.round(s.exit_value)) }));
      } catch (e) { toast.error(e?.response?.data?.detail || "Could not compute"); }
      finally { setBusy(false); }
    }, 300);
    return () => clearTimeout(timer.current);
  }, [form, assumptions]);

  const submitLead = async (e) => {
    e.preventDefault();
    setLeadBusy(true);
    try {
      const { data } = await api.post("/public/option-calculator/lead", { ...lead, role: "employee", ...toPayload(form, assumptions) });
      setLeadDone(true);
      const message=data.emailed ? "Email request processed. Check your inbox." : "Analysis saved, but the email could not be sent.";
      setLeadStatus(message); toast.message(message);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not send"); }
    finally { setLeadBusy(false); }
  };

  return (
    <MarketingLayout>
      <PageHero eyebrow="Free, no sign-up required" title="Make sense of" accent="your stock options."
        subtitle="Explore how preferences, dilution, exercise costs, and tax assumptions can shape an equity scenario. A useful model, not a promise of future value." />
      <section className="mx-auto max-w-7xl px-5 py-12 sm:px-8" data-testid="option-calculator">
        <div className="grid min-w-0 grid-cols-1 gap-8 lg:grid-cols-[320px_minmax(0,1fr)] xl:grid-cols-[360px_minmax(0,1fr)]">
          <Card className="min-w-0 h-fit rounded-xl border bg-white p-5 lg:sticky lg:top-24" data-testid="calc-inputs">
            <h2 className="ff-cabinet mb-1 flex items-center gap-2 text-lg font-bold text-slate-900"><Calculator className="h-4 w-4 text-brand-ink" /> Your grant</h2>
            <p className="mb-4 text-xs text-slate-500">Everything here is editable. Ask HR for the 409A price and the last round's post-money.</p>
            <div className="grid grid-cols-1 gap-3 min-[480px]:grid-cols-2">
              <Num label="Options granted" k="options" form={form} set={set} step="1" testid="calc-options" />
              <Num label="Strike price" k="strike" form={form} set={set} prefix="$" testid="calc-strike" />
              <div>
                <Label htmlFor="calc-option-type" className="text-xs">Option type</Label>
                <Select value={form.option_type} onValueChange={(v) => set("option_type", v)}>
                  <SelectTrigger id="calc-option-type" className="mt-1 h-9 text-sm" data-testid="calc-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="ISO" data-testid="calc-type-iso">ISO</SelectItem><SelectItem value="NSO" data-testid="calc-type-nso">NSO</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Vesting start</Label><DatePicker value={form.vesting_start} onChange={(v) => set("vesting_start", v)} className="mt-1 h-9" data-testid="calc-vesting-start" /></div>
              <Num label="Vesting (months)" k="vesting_months" form={form} set={set} step="1" testid="calc-vesting-months" />
              <Num label="Cliff (months)" k="cliff_months" form={form} set={set} step="1" testid="calc-cliff" />
            </div>
            <h2 className="ff-cabinet mb-1 mt-6 text-lg font-bold text-slate-900">The company</h2>
            <div className="grid grid-cols-1 gap-3 min-[480px]:grid-cols-2">
              <Num label="Last post-money valuation" k="post_money" form={form} set={set} prefix="$" testid="calc-post-money" />
              <Num label="Fully diluted shares" k="fd_shares" form={form} set={set} step="1" testid="calc-fd-shares" />
              <Num label="409A FMV / share" k="fmv" form={form} set={set} prefix="$" testid="calc-fmv" hint="Common stock fair market value" />
              <Num label="Total preferred invested" k="preferred_invested" form={form} set={set} prefix="$" testid="calc-pref-invested" hint="Sits ahead of common at exit" />
              <Num label="Liquidation multiple" k="liquidation_multiple" form={form} set={set} testid="calc-liq-multiple" hint="1× is standard" />
            </div>
            <form onSubmit={submitLead} className="mt-6 rounded-xl border border-neutral-300 bg-neutral-100 p-4" data-testid="calc-lead-form">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-900"><Mail className="h-4 w-4 text-brand-ink" /> Email me this analysis</div>
              <p className="mt-1 text-[11px] text-slate-500">A copy of the numbers above, plus a link to re-run them later.</p>
              {leadDone ? <p role="status" className="mt-3 text-sm font-semibold text-neutral-800" data-testid="calc-lead-done">{leadStatus}</p> : (
                <div className="mt-3 space-y-2">
                  <Input aria-label="Your name for the analysis" placeholder="Your name" value={lead.name} onChange={(e) => setLead((l) => ({ ...l, name: e.target.value }))} className="h-9 bg-white text-sm" data-testid="calc-lead-name" />
                  <Input aria-label="Email address for your analysis" type="email" required placeholder="you@company.com" value={lead.email} onChange={(e) => setLead((l) => ({ ...l, email: e.target.value }))} className="h-9 bg-white text-sm" data-testid="calc-lead-email" />
                  <Button type="submit" disabled={leadBusy} className="w-full gap-2 rounded-full bg-neutral-900 text-white hover:bg-neutral-800" data-testid="calc-lead-submit">{leadBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Send my analysis</Button>
                </div>
              )}
            </form>
          </Card>
          <div className="min-w-0">
            {valuation ? (
              <OptionValuationView valuation={valuation} assumptions={assumptions} onAssumptionsChange={setAssumptions} loading={busy}>
                <ExercisePlanner defaultFmv={form.fmv} deps={[JSON.stringify(form), assumptions.tax_mode, assumptions.simple_rate_pct, assumptions.amt_rate_pct, assumptions.ordinary_rate_pct, assumptions.state_rate_pct]}
                  compute={(p) => api.post("/public/option-exercise-plan", { ...p, ...toPayload(form, assumptions) }).then((r) => r.data)} />
                <Card className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-neutral-900 bg-neutral-900 p-6 text-white" data-testid="calc-founder-cta">
                  <div>
                    <div className="ff-cabinet text-lg font-bold">Founders: give every employee a live version of this.</div>
                    <p className="mt-1 text-sm text-white/75">TenX turns your cap table into a private equity page per grant - vesting, scenarios and tax, always current.</p>
                  </div>
                  <Button asChild className="rounded-full bg-brand text-brand-foreground hover:bg-brand-hover"><Link to="/register" data-testid="calc-founder-cta-link">Start free <ArrowRight className="ml-1 h-4 w-4" /></Link></Button>
                </Card>
              </OptionValuationView>
            ) : <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>}
          </div>
        </div>
      </section>
      <CalculatorExplainer />
    </MarketingLayout>
  );
}
